import { useCallback } from "react";
import { useAuthStoreBase } from "@fmc-digital/core-auth";
import { trackLoginEvent, LoginEvent } from "@/utils/analytics.utils";
import {
  setAnalyticsOktaId,
  syncAnalyticsOktaIdFromSession,
} from "@/utils/analyticsUserContext";
import type { FactorState, OtpValidationResult } from "@fmc-digital/profile";

const LOCKED_ERROR_CODES = new Set(["E0000069", "E0000047"]);
const MFA_MISSING_CONTEXT_ERROR = "MFA verification failed: missing factor or state token.";

type VerifyContext = {
  factorId?: string;
  stateToken?: string;
  link?: string;
};

function resolveFactor(factor: FactorState | undefined, selectedFactor: FactorState | null) {
  return factor ?? selectedFactor ?? undefined;
}

function buildVerifyContext(
  auth: { mfaState?: { stateToken?: string } | null },
  factorToUse: FactorState | undefined,
): VerifyContext {
  const storeSnapshot = useAuthStoreBase.getState();

  return {
    stateToken: auth.mfaState?.stateToken || storeSnapshot.stateToken || undefined,
    factorId: factorToUse?.selectedFactorId || storeSnapshot.selectedFactor?.id,
    link: factorToUse?.link || storeSnapshot.selectedFactor?.link,
  };
}

function resetVerificationState(
  setIsVerifying: (value: boolean) => void,
  setIsRedirecting: (value: boolean) => void,
) {
  setIsVerifying(false);
  setIsRedirecting(false);
}

function extractErrorCode(err: unknown): string | undefined {
  if (typeof err === "object" && err !== null && "errorCode" in err) {
    return (err as { errorCode?: string }).errorCode;
  }
  return undefined;
}

function isLockedError(errorCode: string | undefined, errorMessage: string): boolean {
  const normalizedMessage = errorMessage.toLowerCase();
  return (
    LOCKED_ERROR_CODES.has(errorCode ?? "") ||
    normalizedMessage.includes("locked") ||
    normalizedMessage.includes("too many attempts")
  );
}

function trackFailureEvent(
  isAccountLockedError: boolean,
  factorType?: string,
) {
  try {
    if (isAccountLockedError) {
      trackLoginEvent(LoginEvent.ACCOUNT_LOCKED);
    } else if (factorType === "email") {
      trackLoginEvent(LoginEvent.OTP_EMAIL_VALIDATION_FAILED);
    } else if (factorType === "sms" || factorType === "phone") {
      trackLoginEvent(LoginEvent.OTP_PHONE_VALIDATION_FAILED);
    }
  } catch {}
}

function resolveErrorMessage(
  isAccountLockedError: boolean,
  errorCode: string | undefined,
  t: (key: string, fallback?: string) => string,
): string {
  if (isAccountLockedError) {
    return t("errorCodes.CUST_S1200002");
  }

  if (errorCode === "E0000068") {
    return t("errors.invalidCode", "Invalid verification code");
  }

  return t("errors.genericError", "Something went wrong. Please try again.");
}

type UseMFAVerifyParams = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  auth: any;
  selectedFactor: FactorState | null;
  setIsVerifying: (value: boolean) => void;
  setMfaError: (value: string | null) => void;
  setMfaErrorCode: (value: string | null) => void;
  setIsRedirecting: (value: boolean) => void;
  handleSuccessfulLoginRedirect: (sessionToken: string | null) => void;
  t: (key: string, fallback?: string) => string;
};

export function useMFAVerify({
  auth,
  selectedFactor,
  setIsVerifying,
  setMfaError,
  setMfaErrorCode,
  setIsRedirecting,
  handleSuccessfulLoginRedirect,
  t,
}: UseMFAVerifyParams) {
  const handleValidate = useCallback(
    async (code: string): Promise<OtpValidationResult> => {
      setMfaError(null);
      setMfaErrorCode(null);

      const factorToUse = resolveFactor(undefined, selectedFactor);
      const { stateToken, factorId, link } = buildVerifyContext(auth, factorToUse);

      try {
        if (!factorId || !stateToken) {
          setMfaError(MFA_MISSING_CONTEXT_ERROR);
          resetVerificationState(setIsVerifying, setIsRedirecting);
          throw new Error(MFA_MISSING_CONTEXT_ERROR);
        }

        setIsVerifying(true);
        const verifyRequest = { factorId, stateToken, passCode: code, link };
        await auth.verifyMFA(verifyRequest);
        setIsVerifying(false);
        
        // Track OTP validation success
        try {
          const factorType = factorToUse?.factorType;
          if (factorType === "email") {
            trackLoginEvent(LoginEvent.OTP_EMAIL_VALIDATION_SUCCESS);
          } else if (factorType === "sms" || factorType === "phone") {
            trackLoginEvent(LoginEvent.OTP_PHONE_VALIDATION_SUCCESS);
          }
        } catch {}
        
        return { status: "SUCCESS" };
      } catch (err) {
        const errorCode = extractErrorCode(err);
        const rawErrorMessage = err instanceof Error ? err.message : "";
        const factorType = factorToUse?.factorType;
        const isAccountLockedError = isLockedError(errorCode, rawErrorMessage);
        const userFacingMessage = resolveErrorMessage(
          isAccountLockedError,
          errorCode,
          t,
        );

        trackFailureEvent(isAccountLockedError, factorType);

        setMfaError(userFacingMessage);
        setMfaErrorCode(errorCode ?? null);
        resetVerificationState(setIsVerifying, setIsRedirecting);
        throw err;
      }
    },
    [auth, selectedFactor, setIsVerifying, setMfaError, setMfaErrorCode, setIsRedirecting, t],
  );

  const handleVerifySuccess = useCallback(
    async (_code: string, _factor: FactorState) => {
      syncAnalyticsOktaIdFromSession();
      const storeOktaId = useAuthStoreBase.getState().oktaId;
      if (storeOktaId) {
        setAnalyticsOktaId(storeOktaId);
      }

      try {
        trackLoginEvent(LoginEvent.SUCCESS);
      } catch {}

      setIsRedirecting(true);
      await new Promise((resolve) => setTimeout(resolve, 100));

      const sessionToken = sessionStorage.getItem("okta_session_token");
      handleSuccessfulLoginRedirect(sessionToken);
    },
    [handleSuccessfulLoginRedirect, setIsRedirecting],
  );

  return { handleValidate, handleVerifySuccess };
}
